import moment from 'moment';
import {WarningIcon} from 'web-components/icons';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {PROJECT_STATUS} from '../../../constants';

const getEndDateWarningInfoAndVariant = (predictedEndDate, optimisticPredictedEndDate, projectEndDate, intl) => {
	const predictedEndDateMoment = moment(predictedEndDate);
	const optimisticPredictedEndDateMoment = moment(optimisticPredictedEndDate);
	const endDateDifference = projectEndDate && predictedEndDateMoment.diff(projectEndDate, 'days');

	if (!predictedEndDate) {
		return {info: `Project predicted to not have enough resources to finish`, variant: WarningIcon.VARIANT.DANGER};
	}
	if (!projectEndDate) {
		const predictedEndDateFormatted = predictedEndDateMoment.format('LL');
		return {
			info: `${predictedEndDateFormatted}`,
			infoExpanded: `The project is predicted to be finished on ${predictedEndDateFormatted}.`,
			variant: WarningIcon.VARIANT.NO_WARNING,
		};
	}
	if (endDateDifference < 0) {
		return {
			info: `${Math.abs(endDateDifference)} days ahead`,
			infoExpanded: `The project is predicted to be finished ${endDateDifference} days early.`,
			variant: WarningIcon.VARIANT.NO_WARNING,
		};
	}
	if (endDateDifference > 0) {
		const dangerVariant = optimisticPredictedEndDateMoment.isAfter(projectEndDate, 'day');
		return {
			info: dangerVariant
				? `Significant delay of ${endDateDifference} days`
				: `Slight delay of ${endDateDifference} days`,
			infoExpanded: `The project is predicted to be finished ${endDateDifference} days late.`,
			variant: dangerVariant ? WarningIcon.VARIANT.DANGER : WarningIcon.VARIANT.WARNING,
		};
	}
	return {
		info: `On time`,
		infoExpanded: `The project is predicted to be finished on time.`,
		variant: WarningIcon.VARIANT.NO_WARNING,
	};
};

export const getFocusInfoAndVariant = focus => {
	if (focus < 0.7) {
		return {info: `Team is working on the project significantly less than allocated`, variant: WarningIcon.VARIANT.DANGER};
	}
	if (focus < 0.9) {
		return {info: `Team is working on this project less than allocated`, variant: WarningIcon.VARIANT.WARNING};
	}
	if (focus > 1.3) {
		return {info: `Team is working on this project way more than allocated`, variant: WarningIcon.VARIANT.WARNING};
	}

	return {info: `Team is working on this project as allocated`, variant: WarningIcon.VARIANT.NO_WARNING};
};

export const getPerformanceInfoAndVariant = performance => {
	if (performance < 0.7) {
		return {info: `Tasks are taking significantly longer to complete than estimated`, variant: WarningIcon.VARIANT.DANGER};
	}
	if (performance < 0.9) {
		return {info: `Tasks are taking longer to complete than estimated`, variant: WarningIcon.VARIANT.WARNING};
	}
	if (performance > 1.3) {
		return {
			info: `Tasks are taking significantly less time to complete than estimated`,
			variant: WarningIcon.VARIANT.WARNING,
		};
	}

	return {info: `Tasks are completed close to their estimates`, variant: WarningIcon.VARIANT.NO_WARNING};
};

export const getPredictedEndDateData = (project, projectEndDate, predictedEndDateError, intl) => {
	if (project.status === PROJECT_STATUS.OPPORTUNITY) {
		return {
			title: 'Predicted Project End',
			info: 'Not available in Opportunity',
		};
	} else if (project.status === PROJECT_STATUS.DONE) {
		return {
			title: 'Predicted Project End',
			info: 'Project is Done',
			warningVariant: WarningIcon.VARIANT.NO_WARNING,
			actionCallback: () =>
				showModal({
					type: MODAL_TYPE.PREDICTED_END_DATE,
					project: project,
					endDateInfo: 'Project is Done',
				}),
		};
	}
	if (predictedEndDateError) {
		return {
			title: 'Predicted Project End',
			info: predictedEndDateError,
		};
	}

	const predictedEndDate = project.projectHealth.predictedEndDate;
	const optimisticPredictedEndDate = project.projectHealth.optimisticPredictedEndDate;
	const warningInfo = getEndDateWarningInfoAndVariant(predictedEndDate, optimisticPredictedEndDate, projectEndDate, intl);
	return {
		title: 'Predicted Project End',
		warningVariant: warningInfo.variant,
		info: warningInfo.info,
		actionCallback: () =>
			showModal({
				type: MODAL_TYPE.PREDICTED_END_DATE,
				project: project,
				endDateInfo: warningInfo.infoExpanded || warningInfo.info,
			}),
	};
};

export const getPerformanceData = (project, performance, performanceError, intl) => {
	if (project.status === PROJECT_STATUS.OPPORTUNITY) {
		return {
			title: 'Performance',
			info: 'Not available in Opportunity',
		};
	}
	if (performanceError) {
		return {
			title: 'Performance',
			info: performanceError,
		};
	}
	const warningInfo = getPerformanceInfoAndVariant(performance);
	return {
		title: 'Performance',
		warningVariant: warningInfo.variant,
		info: warningInfo.info,
		actionCallback: () =>
			showModal({
				type: MODAL_TYPE.PERFORMANCE,
				projectId: project.id,
				performance: performance,
				warningInfo: warningInfo,
			}),
	};
};

export const getFocusData = (project, focus, focusError, intl) => {
	if (project.status === PROJECT_STATUS.OPPORTUNITY) {
		return {
			title: 'Focus',
			info: 'Not available in Opportunity',
		};
	}
	if (focusError) {
		return {
			title: 'Focus',
			info: focusError,
		};
	}
	const warningInfo = getFocusInfoAndVariant(focus);
	const focusFormatted = intl.formatNumber(focus * 100) + '%';
	return {
		title: 'Team Focus',
		warningVariant: warningInfo.variant,
		info: warningInfo.info,
		actionCallback: () =>
			showModal({
				type: MODAL_TYPE.TEAM_FOCUS,
				projectId: project.id,
				focus: focusFormatted,
				warningInfo: warningInfo,
			}),
	};
};

export const getSuggestedStatus = warnings => {
	if (warnings.filter(warning => warning === WarningIcon.VARIANT.DANGER).length > 0) {
		return WarningIcon.VARIANT.DANGER;
	} else if (warnings.filter(warning => warning === WarningIcon.VARIANT.WARNING).length > 1) {
		return WarningIcon.VARIANT.WARNING;
	}

	return WarningIcon.VARIANT.NO_WARNING;
};
